import React, { useEffect } from "react";
import { create } from "zustand";
import { X } from "lucide-react";
import { useDispatch } from "react-redux";
import { useComposedQueryInfo } from "@/components/ReferenceWrapper/services/useQueryInfo";
import { getSourceMapForQid } from "@/components/ReferenceWrapper/services/getSourceRefs";
import clsx from "clsx";
import MarkdownRenderer from "@/components/common/MarkDownRenderer";
import { setFileViewerData } from "@/redux/fileViewerSlice";

interface AnswerStore {
  visible: boolean;
  workspaceId: "" | string;
  qid: "" | string;
  heading: string;
  open(workspaceId: string, qid: string, heading?: string): void;
  close(): void;
}

export const useAnswerStore = create<AnswerStore>((set) => ({
  visible: false,
  workspaceId: "",
  qid: "",
  heading: "",
  open: (workspaceId, qid, heading = "") =>
    set({ visible: true, workspaceId, qid, heading }),
  close: () => set({ visible: false }),
}));

const formatAnswerDate = (raw: unknown): string | null => {
  if (typeof raw !== "string" || raw.length === 0) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const AnswerDialog = React.memo(() => {
  const visible = useAnswerStore((s) => s.visible);
  const id = useAnswerStore((s) => s.qid);
  const close = useAnswerStore((s) => s.close);
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, close]);
  return (
    <div
      onClick={close}
      className={clsx(
        "fixed inset-0 z-50 flex items-center justify-center bg-black/40 transition-opacity",
        !visible && "hidden"
      )}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[800px] max-w-[95vw] h-[80vh] bg-white border border-gray-300 rounded-lg shadow-xl flex flex-col overflow-hidden relative"
      >
        <AnswerDialogImpl key={id} />
      </div>
    </div>
  );
});

export const AnswerDialogImpl = () => {
  const visible = useAnswerStore((s) => s.visible);
  const workspaceId = useAnswerStore((s) => s.workspaceId);
  const qid = useAnswerStore((s) => s.qid);
  const heading = useAnswerStore((s) => s.heading);
  const close = useAnswerStore((s) => s.close);
  const dispatch = useDispatch();

  const { data } = useComposedQueryInfo(workspaceId, qid);

  const finalAnswer =
    data?.answer?.finalAnswer || data?.toolResponse?.answer?.finalAnswer || "";
  // Prefer the per-section response text when present: those bodies carry
  // the [N.N] citation tokens that pair with the query's source map, so
  // the modal renders a cited answer with clickable refs. The
  // top-level finalAnswer is sometimes a citation-less collapse — RMS
  // autofill, for instance, runs document_search to get sectionResponses
  // (with citations) and then a super_short_summary step that overwrites
  // finalAnswer with the bare extracted value. Falls back to finalAnswer
  // when no sections exist (older agents, simple chat queries, etc.).
  type Section = { sectionTitle?: string; response: string };
  const sectionResponses: Section[] | undefined =
    data?.toolResponse?.answer?.sectionResponses ||
    (data?.answer as { sectionResponses?: Section[] } | undefined)
      ?.sectionResponses;
  // The document_search agent stuffs the literal list of retrieved
  // filenames into `sectionTitle` ("Results from documents: a.pdf,
  // b.pdf, …") rather than putting a useful heading there. Treat any
  // such title as noise — the source-map already gives clickable
  // per-citation document refs, so the bulk filename dump just buries
  // the answer. Same prefix can also appear at the head of the body
  // (older agent versions); strip that too.
  const isFilenameDumpTitle = (title: string | undefined): boolean =>
    !!title && title.trimStart().startsWith("Results from documents:");
  const stripResultsFromDocuments = (text: string): string => {
    const pattern = /(^|\n)\s*Results from documents:[^\n]*(\n+|$)/g;
    return text.replace(pattern, "\n").trim();
  };
  const renderedContent = (() => {
    if (sectionResponses && sectionResponses.length > 0) {
      // Autofill answers should always be a single section (the
      // backend now enforces this). Older queries may have shipped
      // with 2+ sections from the multi-section path; show only the
      // first to keep the modal coherent — joining them just dumps
      // overlapping prose with two filename-block titles, which
      // reads as noise.
      const section = sectionResponses[0];
      const rawTitle = section.sectionTitle?.trim();
      const title = isFilenameDumpTitle(rawTitle) ? "" : rawTitle;
      const body = stripResultsFromDocuments(section.response ?? "");
      return title ? `## ${title}\n\n${body}` : body;
    }
    return finalAnswer;
  })();
  const answerDate = formatAnswerDate(
    (data?.toolResponse as { created_at?: unknown } | undefined)?.created_at
  );
  const title = heading || "Answer Details";

  // Intercept citation-click events at the capture phase so the document
  // opens in the global DocumentViewDrawer (same as /documents) instead
  // of Layout's split pane. stopImmediatePropagation prevents Layout's
  // bubble-phase handler from firing for this modal session; then we
  // dispatch setFileViewerData with doNotOpenDrawer=false so the
  // already-mounted DocumentViewDrawer slides open and renders the doc.
  // Only while the modal is actually up. The wrapper hides itself with a CSS
  // class but keeps this component mounted, so an unconditional listener kept
  // intercepting on every surface that renders an AnswerDialog — which is
  // every RMS table view. That silently forced RMS document clicks into the
  // overlay drawer no matter what the viewer-mode setting said, because the
  // event never reached Layout at all (#9746).
  useEffect(() => {
    if (!visible) return;
    const handler = async (evt: Event) => {
      evt.stopImmediatePropagation();
      const e = evt as CustomEvent;
      const detail = e.detail ?? {};
      const {
        fileId,
        fileName,
        fileType,
        citationId,
        queryId,
        originalNumValue,
        parentElementText,
      } = detail;

      if (fileId && fileName && fileType) {
        dispatch(
          setFileViewerData({
            isOpen: true,
            fileId,
            fileName,
            fileType,
            doNotOpenDrawer: false,
            highlightContext: detail.highlightContext,
            highlightPageNum: detail.highlightPageNum,
            originalNumValue: detail.originalNumValue,
            parentElementText: detail.parentElementText,
            highlightCell: detail.highlightCell,
          })
        );
        return;
      }

      if (!citationId || !queryId) return;
      try {
        const sourceMap = await getSourceMapForQid(queryId);
        const firstCid =
          String(citationId)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)[0] ?? citationId;
        const refs = sourceMap[`[${firstCid}]`] ?? sourceMap[firstCid];
        const ref = refs?.[0] as any;
        if (!ref || ref.rmsEntity || ref.interactionId) return;

        const highlightValue = originalNumValue || ref.originalNumValue;
        dispatch(
          setFileViewerData({
            isOpen: true,
            fileId: ref.parentDocId,
            fileName:
              ref.blobFilename || ref.docTitle || ref.parentDoc?.docTitle,
            fileType: ref.blobType,
            doNotOpenDrawer: false,
            highlightContext: highlightValue || ref.smallContext || ref.content,
            highlightPageNum: ref.pageNum,
            originalNumValue: highlightValue,
            parentElementText,
          })
        );
      } catch (err) {
        console.error("AnswerDialog citation lookup failed:", err);
      }
    };
    window.addEventListener("openDocumentPanel", handler, true);
    return () => {
      window.removeEventListener("openDocumentPanel", handler, true);
    };
  }, [dispatch, visible]);

  return (
    <>
      <div className="px-5 py-3 flex justify-between items-start border-b border-gray-200 bg-white">
        <div className="min-w-0 pr-4">
          <div className="text-base font-semibold text-gray-900 truncate">
            {title}
          </div>
          {/* Reserve the date row's height so the header doesn't jump when
              the answer metadata finishes loading. */}
          <div className="mt-0.5 text-xs text-gray-500 h-4 leading-4">
            {answerDate ? `As of ${answerDate}` : " "}
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700"
          onClick={close}
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-6 bg-white">
        {data?.status === "failed" ? (
          <div className="w-full p-4 text-red-500 flex items-center justify-center bg-red-50 rounded-md">
            <span className="font-medium">
              Query execution failed. Please try again.
            </span>
          </div>
        ) : renderedContent ? (
          <div
            className="flex justify-start relative"
            data-matrix-answer-viewer
          >
            <MarkdownRenderer
              content={renderedContent}
              queryId={qid}
              status="completed"
              isUserMessage={false}
              forceShowCitations
            />
          </div>
        ) : (
          <div className="w-full h-[200px] flex items-center justify-center">
            <div className="animate-pulse text-gray-500 font-medium">
              Loading...
            </div>
          </div>
        )}
      </div>
    </>
  );
};
